import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { axiosInstance } from '../config';
import RecipePriceDetails from '../components/RecipePriceDetails';
import SignInPopup from '../components/SignInPopup';
import { useSignInPopup } from '../hooks/useSignInPopup';
import { useGetUserID } from '../hooks/useGetUserID';
import { formatNutritionValue } from '../utils/nutritionCalculator';
import '../styles/recipe-price-details-modern.css';

const MealPlanDetails = () => {
    const { planId } = useParams();
    const navigate = useNavigate();
    const [mealPlan, setMealPlan] = useState(null);
    const [loading, setLoading] = useState(true);
    const [recipeDetails, setRecipeDetails] = useState({});
    const [loadingRecipeDetails, setLoadingRecipeDetails] = useState(false);
    const [consolidatedIngredients, setConsolidatedIngredients] = useState([]);

    const [showConsolidatedPricing, setShowConsolidatedPricing] = useState(false);
    const [loadingConsolidatedCost, setLoadingConsolidatedCost] = useState(false);
    const [consolidatedIngredientPrices, setConsolidatedIngredientPrices] = useState({});
    const [consolidatedTotalCost, setConsolidatedTotalCost] = useState(0);
    const [cupboardSavings, setCupboardSavings] = useState(null);
    const [displayTotalCost, setDisplayTotalCost] = useState(null);
    const [ingredientsDropdownOpen, setIngredientsDropdownOpen] = useState(false);
    const [cupboardIngredients, setCupboardIngredients] = useState([]);
    const [isAddingToCupboard, setIsAddingToCupboard] = useState(false);

    const [isEditingDetails, setIsEditingDetails] = useState(false);
    const [editName, setEditName] = useState('');
    const [editStartDate, setEditStartDate] = useState('');
    const [editEndDate, setEditEndDate] = useState('');
    const [editDescription, setEditDescription] = useState('');
    const [savingDetails, setSavingDetails] = useState(false);
    const [togglingPublic, setTogglingPublic] = useState(false);
    const [nutritionPreview, setNutritionPreview] = useState(null);
    const [loadingNutritionPreview, setLoadingNutritionPreview] = useState(false);

    const userID = useGetUserID();
    const getAuthToken = () => window.localStorage.getItem('auth_token');
    const isAuthenticated = !!(userID && getAuthToken());
    const { showPopup: showSignInPopup, handleClosePopup: handleCloseSignInPopup, openPopup: openSignInPopup } = useSignInPopup(isAuthenticated, 'mealplandetails');

    useEffect(() => {
        loadMealPlan();
    }, [planId]);

    // Scroll to top when entering this page (e.g. from AIMealPlanning after saving a plan)
    useEffect(() => {
        window.scrollTo(0, 0);
    }, [planId]);

    useEffect(() => {
        if (consolidatedIngredients.length > 0 && !showConsolidatedPricing) {
            calculateConsolidatedCost();
        }
    }, [consolidatedIngredients]);

    const fetchCupboardIngredients = useCallback(async () => {
        if (!userID || !getAuthToken()) return;
        try {
            const response = await axiosInstance.get(`/cupboard/${userID}`);
            if (response.data.success && response.data.items) {
                setCupboardIngredients(response.data.items);
            }
        } catch (error) {
            console.error('Error fetching cupboard:', error);
        }
    }, [userID]);

    const handleAddIngredientToCupboard = async (ingredient) => {
        if (!userID || !getAuthToken()) {
            openSignInPopup();
            return;
        }
        const amount = ingredient.amount ?? ingredient.amounts?.[0]?.amount ?? 0;
        const unit = ingredient.unit ?? ingredient.amounts?.[0]?.unit ?? 'unit';
        setIsAddingToCupboard(true);
        try {
            await axiosInstance.post('/cupboard/add', {
                ingredientName: ingredient.name,
                amount,
                unit,
                location: 'cupboard'
            });
            await fetchCupboardIngredients();
        } catch (error) {
            console.error('Error adding to cupboard:', error);
        } finally {
            setIsAddingToCupboard(false);
        }
    };

    const isIngredientInCupboard = (ingredientName) =>
        cupboardIngredients.some(
            (item) => item.ingredientName?.toLowerCase() === ingredientName?.toLowerCase()
        );

    useEffect(() => {
        if (ingredientsDropdownOpen && userID && consolidatedIngredients.length > 0) {
            fetchCupboardIngredients();
        }
    }, [ingredientsDropdownOpen, userID, consolidatedIngredients.length, fetchCupboardIngredients]);

    useEffect(() => {
        if (consolidatedIngredients.length > 0 && userID && getAuthToken()) {
            fetchCupboardIngredients();
        }
    }, [consolidatedIngredients.length, userID, fetchCupboardIngredients]);

    const toDateInputValue = (date) => {
        if (!date) return '';
        const d = new Date(date);
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${y}-${m}-${day}`;
    };

    const loadMealPlan = async () => {
        setLoading(true);
        try {
            const response = await axiosInstance.get(`/meal-planning/plans/${planId}`);
            if (response.data.success) {
                const plan = response.data.mealPlan;
                setMealPlan(plan);
                setEditName(plan.name || '');
                setEditStartDate(toDateInputValue(plan.startDate));
                setEditEndDate(toDateInputValue(plan.endDate));
                setEditDescription(plan.description || '');
                setConsolidatedIngredients(response.data.consolidatedIngredients || []);
                loadRecipeDetails(plan.recipes);
            }
        } catch (error) {
            console.error('Error loading meal plan:', error);
            alert('Failed to load meal plan');
            navigate('/meal-planning');
        } finally {
            setLoading(false);
        }
    };

    const startEditingDetails = () => {
        if (mealPlan) {
            setEditName(mealPlan.name || '');
            setEditStartDate(toDateInputValue(mealPlan.startDate));
            setEditEndDate(toDateInputValue(mealPlan.endDate));
            setEditDescription(mealPlan.description || '');
        }
        setIsEditingDetails(true);
    };

    const cancelEditingDetails = () => {
        setIsEditingDetails(false);
    };

    const togglePublic = async () => {
        if (!planId || !mealPlan) return;
        setTogglingPublic(true);
        try {
            const newValue = !mealPlan.isPublic;
            const response = await axiosInstance.put(`/meal-planning/plans/${planId}`, {
                isPublic: newValue
            });
            if (response.data.success) {
                setMealPlan(prev => ({ ...prev, isPublic: newValue }));
            }
        } catch (error) {
            console.error('Error toggling public status:', error);
            alert('Failed to update visibility');
        } finally {
            setTogglingPublic(false);
        }
    };

    const saveDetails = async () => {
        if (!planId || !mealPlan) return;
        setSavingDetails(true);
        try {
            const response = await axiosInstance.put(`/meal-planning/plans/${planId}`, {
                name: editName.trim() || mealPlan.name,
                description: editDescription.trim(),
                startDate: editStartDate || toDateInputValue(mealPlan.startDate),
                endDate: editEndDate || toDateInputValue(mealPlan.endDate)
            });
            if (response.data.success && response.data.mealPlan) {
                const updated = response.data.mealPlan;
                setMealPlan(prev => ({
                    ...prev,
                    name: updated.name,
                    description: updated.description,
                    startDate: updated.startDate,
                    endDate: updated.endDate
                }));
                setIsEditingDetails(false);
            }
        } catch (error) {
            console.error('Error saving meal plan details:', error);
            alert('Failed to save details');
        } finally {
            setSavingDetails(false);
        }
    };

    const loadRecipeDetails = async (recipes) => {
        setLoadingRecipeDetails(true);
        try {
            const userId = window.localStorage.getItem('userID');
            const recipeConfigs = recipes.map(recipe => ({
                recipeId: recipe.recipeId._id,
                servings: recipe.servings
            }));

            const response = await axiosInstance.post('/meal-planning/calculate-batch-cost', {
                recipes: recipeConfigs,
                userId: userId,
                preferredRegion: window.localStorage.getItem('preferredRegion') || null
            });

            if (response.data.success) {
                const details = {};
                response.data.recipes.forEach(recipeResult => {
                    details[recipeResult.recipeId] = {
                        name: recipeResult.recipeName,
                        servings: recipeResult.servings,
                        pricing: recipeResult.pricing,
                        ingredients: recipes.find(r => r.recipeId._id === recipeResult.recipeId)?.recipeId?.ingredients || []
                    };
                });
                setRecipeDetails(details);
            }
        } catch (error) {
            console.error('Error loading recipe details:', error);
            const details = {};
            for (const recipe of recipes) {
                try {
                    const response = await axiosInstance.get(
                        `/meal-planning/recipe/${recipe.recipeId._id}`,
                        { params: { servings: recipe.servings } }
                    );
                    if (response.data.success) {
                        details[recipe.recipeId._id] = response.data.recipe;
                    }
                } catch (err) {
                    console.error(`Error loading recipe ${recipe.recipeId._id}:`, err);
                }
            }
            setRecipeDetails(details);
        } finally {
            setLoadingRecipeDetails(false);
        }
    };

    const loadNutritionPreview = useCallback(async (recipes) => {
        if (!recipes?.length) return;
        setLoadingNutritionPreview(true);
        setNutritionPreview(null);
        try {
            const totalServings = recipes.reduce((sum, r) => sum + (r.servings || 0), 0);
            const results = await Promise.allSettled(
                recipes.map((recipe) =>
                    axiosInstance.post(`/recipes/${recipe.recipeId._id}/calculate-nutrition`, {
                        servings: recipe.servings
                    })
                )
            );
            const totals = {
                Calories: 0,
                Protein: 0,
                Carbs: 0,
                Fats: 0,
                Sodium: 0,
                Sugar: 0,
                Fiber: 0
            };
            results.forEach((result) => {
                if (result.status === 'fulfilled' && result.value?.data?.total) {
                    const t = result.value.data.total;
                    totals.Calories += t.Calories || 0;
                    totals.Protein += t.Protein || 0;
                    totals.Carbs += t.Carbs || 0;
                    totals.Fats += t.Fats || 0;
                    totals.Sodium += t.Sodium || 0;
                    totals.Sugar += t.Sugar || 0;
                    totals.Fiber += t.Fiber || 0;
                }
            });
            setNutritionPreview({
                total: totals,
                perServing: totalServings > 0 ? {
                    Calories: Math.round(totals.Calories / totalServings),
                    Protein: parseFloat((totals.Protein / totalServings).toFixed(1)),
                    Carbs: parseFloat((totals.Carbs / totalServings).toFixed(1)),
                    Fats: parseFloat((totals.Fats / totalServings).toFixed(1)),
                    Sodium: Math.round(totals.Sodium / totalServings),
                    Sugar: parseFloat((totals.Sugar / totalServings).toFixed(1)),
                    Fiber: parseFloat((totals.Fiber / totalServings).toFixed(1))
                } : null,
                totalServings
            });
        } catch (error) {
            console.error('Error loading nutrition preview:', error);
        } finally {
            setLoadingNutritionPreview(false);
        }
    }, []);

    useEffect(() => {
        if (mealPlan?.recipes?.length > 0) {
            loadNutritionPreview(mealPlan.recipes);
        } else {
            setNutritionPreview(null);
        }
    }, [mealPlan?.recipes, loadNutritionPreview]);

    const viewRecipe = (recipeId) => {
        navigate(`/recipe/${recipeId}`);
    };

    const calculateConsolidatedCost = async () => {
        if (consolidatedIngredients.length === 0) {
            alert('No consolidated ingredients to calculate');
            return;
        }

        setLoadingConsolidatedCost(true);
        setShowConsolidatedPricing(true);

        try {
            const userId = window.localStorage.getItem('userID');
            const ingredientsForCalculation = consolidatedIngredients.flatMap(ing => {
                if (ing.amounts && Array.isArray(ing.amounts)) {
                    return ing.amounts.map(amt => ({
                        name: ing.name,
                        amount: amt.amount,
                        unit: amt.unit
                    }));
                }
                return [{
                    name: ing.name,
                    amount: ing.amount || 0,
                    unit: ing.unit || ''
                }];
            });

            const response = await axiosInstance.post(`/meal-planning/calculate-consolidated-cost`, {
                ingredients: ingredientsForCalculation,
                userId: userId,
                preferredRegion: window.localStorage.getItem('preferredRegion') || null
            });

            if (response.data.success) {
                setConsolidatedIngredientPrices(response.data.ingredientPrices);
                setConsolidatedTotalCost(response.data.totalCost);
                setCupboardSavings(response.data.cupboardSavings);
            }
        } catch (error) {
            console.error('Error calculating consolidated cost:', error);
            alert('Failed to calculate consolidated cost');
        } finally {
            setLoadingConsolidatedCost(false);
        }
    };

    const groupRecipesByMealType = () => {
        if (!mealPlan) return {};
        const grouped = { breakfast: [], lunch: [], dinner: [], snack: [] };
        mealPlan.recipes.forEach(recipe => {
            const mealType = recipe.mealType || 'dinner';
            grouped[mealType].push(recipe);
        });
        return grouped;
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="max-w-4xl mx-auto px-3 sm:px-4 py-4 sm:py-6 w-full">
                    <div className="flex flex-col items-center justify-center py-12 text-gray-600">
                        <div className="w-8 h-8 border-2 border-gray-200 border-t-amber-500 rounded-full animate-spin mb-4" />
                        Loading meal plan...
                    </div>
                </div>
            </div>
        );
    }

    if (!mealPlan) {
        return (
            <div className="min-h-screen bg-gray-50">
                <div className="max-w-4xl mx-auto px-3 sm:px-4 py-4 sm:py-6">
                    <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6">
                        <p className="text-gray-600 mb-4">Meal plan not found</p>
                        <button
                            className="inline-flex items-center justify-center px-4 py-2.5 rounded-lg text-sm font-semibold bg-amber-50 border-2 border-amber-700/30 text-amber-800 hover:bg-amber-100 transition"
                            onClick={() => navigate('/meal-planning')}
                        >
                            ← Back to Meal Plans
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    const groupedRecipes = groupRecipesByMealType();
    const fullCost = Object.values(recipeDetails).reduce(
        (sum, recipe) => sum + (recipe.pricing?.totalPrice || 0),
        0
    );
    const totalCost = consolidatedTotalCost > 0 ? consolidatedTotalCost : fullCost;
    const cupboardSavingsAmount = consolidatedTotalCost > 0 && cupboardSavings?.count > 0
        ? fullCost - consolidatedTotalCost
        : 0;
    const totalServings = mealPlan.recipes.reduce((sum, recipe) => sum + recipe.servings, 0);
    const costToDisplay = (showConsolidatedPricing && displayTotalCost != null) ? displayTotalCost : totalCost;
    const cupboardItemsCount = consolidatedIngredients.filter((ing) => isIngredientInCupboard(ing.name)).length;

    return (
        <div className="min-h-screen bg-gray-50">
            <div className="max-w-4xl mx-auto px-3 sm:px-4 py-4 sm:py-6">
                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 pb-6 mb-6 border-b-2 border-gray-100">
                    <div className="flex-1 min-w-0">
                        {!isEditingDetails ? (
                            <>
                                <h1 className="text-xl sm:text-2xl font-extrabold text-gray-900 tracking-tight mb-0.5">
                                    {mealPlan.name}
                                </h1>
                                <p className="text-sm sm:text-base text-gray-600 m-0">
                                    {new Date(mealPlan.startDate).toLocaleDateString()} – {new Date(mealPlan.endDate).toLocaleDateString()}
                                </p>
                            </>
                        ) : (
                            <div className="space-y-3 max-w-md">
                                <div>
                                    <label className="block text-xs font-medium text-gray-600 mb-1">Plan name</label>
                                    <input
                                        type="text"
                                        value={editName}
                                        onChange={(e) => setEditName(e.target.value)}
                                        className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                                        placeholder="Meal plan name"
                                    />
                                </div>
                                <div className="grid grid-cols-2 gap-2">
                                    <div>
                                        <label className="block text-xs font-medium text-gray-600 mb-1">Start date</label>
                                        <input
                                            type="date"
                                            value={editStartDate}
                                            onChange={(e) => setEditStartDate(e.target.value)}
                                            className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-medium text-gray-600 mb-1">End date</label>
                                        <input
                                            type="date"
                                            value={editEndDate}
                                            onChange={(e) => setEditEndDate(e.target.value)}
                                            className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                                        />
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-gray-600 mb-1">Description</label>
                                    <textarea
                                        value={editDescription}
                                        onChange={(e) => setEditDescription(e.target.value)}
                                        rows={2}
                                        className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 resize-none"
                                        placeholder="Optional description"
                                    />
                                </div>
                                <div className="flex gap-2">
                                    <button
                                        type="button"
                                        onClick={saveDetails}
                                        disabled={savingDetails}
                                        className="px-4 py-2 rounded-lg text-sm font-semibold bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-50"
                                    >
                                        {savingDetails ? 'Saving...' : 'Save'}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={cancelEditingDetails}
                                        disabled={savingDetails}
                                        className="px-4 py-2 rounded-lg text-sm font-semibold bg-white border border-gray-200 text-gray-700 hover:bg-gray-50"
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                    <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                        {!isEditingDetails && (
                            <button
                                className="inline-flex items-center justify-center px-4 py-2.5 rounded-lg text-sm font-semibold bg-amber-50 border-2 border-amber-200 text-amber-800 hover:bg-amber-100 transition"
                                onClick={startEditingDetails}
                            >
                                Edit name & dates
                            </button>
                        )}
                        <span
                            className={`inline-flex px-2 py-1 rounded-lg text-xs font-medium uppercase ${
                                mealPlan.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                            }`}
                        >
                            {mealPlan.isActive ? 'Active' : 'Inactive'}
                        </span>
                        <button
                            onClick={togglePublic}
                            disabled={togglingPublic}
                            title={mealPlan.isPublic ? 'Make private — only you can see this plan' : 'Make public — anyone can find this plan'}
                            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border-2 transition disabled:opacity-50 ${
                                mealPlan.isPublic
                                    ? 'bg-blue-50 border-blue-200 text-blue-800 hover:bg-blue-100'
                                    : 'bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100'
                            }`}
                        >
                            {togglingPublic ? (
                                <div className="w-3 h-3 border border-current border-t-transparent rounded-full animate-spin" />
                            ) : mealPlan.isPublic ? (
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                            ) : (
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                </svg>
                            )}
                            {mealPlan.isPublic ? 'Public' : 'Private'}
                        </button>
                        <button
                            className="inline-flex items-center justify-center px-4 py-2.5 rounded-lg text-sm font-semibold bg-white border-2 border-gray-200 text-gray-800 hover:bg-gray-50 transition"
                            onClick={() => navigate('/meal-prep-draft')}
                        >
                            Meal prep draft
                        </button>
                        <button
                            className="inline-flex items-center justify-center px-4 py-2.5 rounded-lg text-sm font-semibold bg-white border-2 border-gray-200 text-gray-800 hover:bg-gray-50 transition"
                            onClick={() => navigate('/meal-planning')}
                        >
                            ← Back to Plans
                        </button>
                    </div>
                </div>

                {mealPlan.description && !isEditingDetails && (
                    <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 sm:p-6 mb-8">
                        <p className="text-gray-600 m-0 text-sm sm:text-base">{mealPlan.description}</p>
                    </div>
                )}

                {/* Summary Stats */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 mb-8">
                    <div className="bg-white p-4 sm:p-6 rounded-xl border border-gray-100 shadow-sm text-center">
                        <div className="text-xl sm:text-2xl font-extrabold text-gray-900 mb-0.5">{mealPlan.recipes.length}</div>
                        <div className="text-xs text-gray-500 uppercase tracking-wide font-medium">Total Recipes</div>
                    </div>
                    <div className="bg-white p-4 sm:p-6 rounded-xl border border-gray-100 shadow-sm text-center">
                        <div className="text-xl sm:text-2xl font-extrabold text-gray-900 mb-0.5">{totalServings}</div>
                        <div className="text-xs text-gray-500 uppercase tracking-wide font-medium">Total Servings</div>
                    </div>
                    <div
                        className={`p-4 sm:p-6 rounded-xl border shadow-sm text-center ${
                            cupboardSavingsAmount > 0
                                ? 'bg-amber-50 border-amber-200'
                                : 'bg-white border-gray-100'
                        }`}
                    >
                        <div className={`text-xl sm:text-2xl font-extrabold mb-0.5 ${cupboardSavingsAmount > 0 ? 'text-amber-800' : 'text-gray-900'}`}>
                            {loadingConsolidatedCost || loadingRecipeDetails ? '...' : `R${costToDisplay.toFixed(2)}`}
                        </div>
                        <div className={`text-xs uppercase tracking-wide font-medium ${cupboardSavingsAmount > 0 ? 'text-amber-700' : 'text-gray-500'}`}>
                            {cupboardSavingsAmount > 0 ? 'Cost (Cupboard Applied)' : 'Estimated Total Cost'}
                        </div>
                    </div>
                    <div className="bg-white p-4 sm:p-6 rounded-xl border border-gray-100 shadow-sm text-center">
                        <div className="text-xl sm:text-2xl font-extrabold text-gray-900 mb-0.5">
                            {loadingConsolidatedCost || loadingRecipeDetails ? '...' : `R${(totalServings > 0 ? costToDisplay / totalServings : 0).toFixed(2)}`}
                        </div>
                        <div className="text-xs text-gray-500 uppercase tracking-wide font-medium">Cost per Serving</div>
                    </div>
                    {cupboardItemsCount > 0 && (
                        <div className="col-span-2 sm:col-span-1 bg-amber-50 border-2 border-amber-200 p-4 sm:p-6 rounded-xl shadow-sm text-center">
                            <div className="text-xl sm:text-2xl font-extrabold text-amber-800 mb-0.5">{cupboardItemsCount}</div>
                            <div className="text-xs text-amber-700 uppercase tracking-wide font-medium">
                                Cupboard Items
                                <span className="block text-[10px] mt-0.5 opacity-90">No purchase needed</span>
                            </div>
                        </div>
                    )}
                </div>

                {/* Nutrition summary */}
                {loadingNutritionPreview && (
                    <div className="flex items-center justify-center gap-2 py-3 mb-6 text-sm text-gray-500">
                        <div className="w-5 h-5 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
                        Loading nutrition…
                    </div>
                )}
                {!loadingNutritionPreview && nutritionPreview?.total && (
                    <div className="bg-white p-4 sm:p-6 rounded-xl border border-gray-100 shadow-sm mb-8">
                        <div className="text-xs text-gray-500 uppercase tracking-wide font-medium mb-3">Nutrition (total plan)</div>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 text-center">
                            <div>
                                <div className="text-lg sm:text-xl font-bold text-gray-900">{formatNutritionValue('Calories', nutritionPreview.total.Calories)}</div>
                                <div className="text-xs text-gray-500">Calories</div>
                            </div>
                            <div>
                                <div className="text-lg sm:text-xl font-bold text-gray-900">{formatNutritionValue('Protein', nutritionPreview.total.Protein)}</div>
                                <div className="text-xs text-gray-500">Protein</div>
                            </div>
                            <div>
                                <div className="text-lg sm:text-xl font-bold text-gray-900">{formatNutritionValue('Carbs', nutritionPreview.total.Carbs)}</div>
                                <div className="text-xs text-gray-500">Carbs</div>
                            </div>
                            <div>
                                <div className="text-lg sm:text-xl font-bold text-gray-900">{formatNutritionValue('Fats', nutritionPreview.total.Fats)}</div>
                                <div className="text-xs text-gray-500">Fats</div>
                            </div>
                        </div>
                        {nutritionPreview.perServing && (
                            <div className="text-xs text-gray-500 mt-3 text-center">
                                Per serving: {formatNutritionValue('Calories', nutritionPreview.perServing.Calories)} · {formatNutritionValue('Protein', nutritionPreview.perServing.Protein)} · {formatNutritionValue('Carbs', nutritionPreview.perServing.Carbs)} · {formatNutritionValue('Fats', nutritionPreview.perServing.Fats)}
                            </div>
                        )}
                    </div>
                )}

                {/* Ingredients checklist dropdown */}
                {consolidatedIngredients.length > 0 && (
                    <div className="mb-8">
                        <button
                            type="button"
                            className="w-full flex items-center justify-between gap-2 p-4 sm:p-5 rounded-xl border-2 border-gray-200 bg-white hover:bg-gray-50 transition text-left"
                            onClick={() => setIngredientsDropdownOpen((prev) => !prev)}
                            aria-expanded={ingredientsDropdownOpen}
                        >
                            <span className="font-semibold text-gray-900">Plan ingredients</span>
                            <svg
                                className={`w-5 h-5 text-gray-500 transition-transform ${ingredientsDropdownOpen ? 'rotate-180' : ''}`}
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                        </button>
                        {ingredientsDropdownOpen && (
                            <div className="mt-2 rounded-xl border-2 border-gray-200 bg-white overflow-hidden shadow-sm">
                                <div className="max-h-80 overflow-y-auto p-3 sm:p-4">
                                    <p className="text-xs text-gray-500 mb-3">
                                        Tap the + to mark an ingredient as &quot;I have it&quot;. Green = in cupboard, grey = need to buy.
                                    </p>
                                    <ul className="space-y-2">
                                        {consolidatedIngredients.map((ingredient, index) => {
                                            const inCupboard = isIngredientInCupboard(ingredient.name);
                                            return (
                                                <li
                                                    key={`${ingredient.name}-${index}`}
                                                    className="flex items-center gap-3 py-2 px-3 rounded-lg hover:bg-gray-50"
                                                >
                                                    <button
                                                        type="button"
                                                        className={`add-to-cupboard-btn ${inCupboard ? 'added' : ''}`}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            if (!inCupboard) handleAddIngredientToCupboard(ingredient);
                                                        }}
                                                        disabled={inCupboard || isAddingToCupboard}
                                                        title={inCupboard ? 'In cupboard' : 'Add to cupboard'}
                                                        aria-label={inCupboard ? 'In cupboard' : 'Add to cupboard'}
                                                    >
                                                        <svg className="cupboard-plus-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16M4 12h16" />
                                                        </svg>
                                                    </button>
                                                    <span className="flex-1 text-sm font-medium text-gray-900 truncate">
                                                        {ingredient.name}
                                                    </span>
                                                    {inCupboard && (
                                                        <span className="text-xs text-green-600 font-medium flex-shrink-0">In cupboard</span>
                                                    )}
                                                </li>
                                            );
                                        })}
                                    </ul>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* Recipes by Meal Type */}
                {Object.entries(groupedRecipes).map(([mealType, recipes]) => {
                    if (recipes.length === 0) return null;
                    return (
                        <div key={mealType} className="mb-8">
                            <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
                                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 p-4 sm:p-6 border-b border-gray-100">
                                    <div>
                                        <h2 className="text-lg font-bold text-gray-900 tracking-tight mb-0.5">
                                            {mealType.charAt(0).toUpperCase() + mealType.slice(1)}
                                        </h2>
                                        <p className="text-sm text-gray-500 m-0">
                                            {recipes.length} recipe{recipes.length !== 1 ? 's' : ''} for {mealType}
                                        </p>
                                    </div>
                                </div>
                                <div className="p-4 sm:p-6">
                                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                        {recipes.map(recipe => {
                                            const details = recipeDetails[recipe.recipeId._id];
                                            return (
                                                <div
                                                    key={recipe.recipeId._id}
                                                    className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden flex flex-col hover:shadow-md transition-all"
                                                >
                                                    <img
                                                        src={recipe.recipeId.imageUrl}
                                                        alt={recipe.recipeId.name}
                                                        className="w-full h-40 sm:h-44 object-cover cursor-pointer hover:scale-105 transition-transform"
                                                        onClick={() => viewRecipe(recipe.recipeId._id)}
                                                    />
                                                    <div className="p-4 flex-1 flex flex-col">
                                                        <h3
                                                            className="font-bold text-gray-900 mb-2 cursor-pointer line-clamp-2 hover:text-amber-700 transition-colors"
                                                            onClick={() => viewRecipe(recipe.recipeId._id)}
                                                        >
                                                            {recipe.recipeId.name}
                                                        </h3>
                                                        <div className="flex gap-3 mb-2 text-xs text-gray-500">
                                                            <span>{recipe.servings} servings</span>
                                                            <span>{recipe.recipeId.cookingTime} min</span>
                                                        </div>

                                                        {details?.pricing?.cupboardIngredientsUsed?.length > 0 && (
                                                            <div className="inline-flex items-center gap-1 px-2 py-1 bg-amber-50 text-amber-800 text-xs font-semibold rounded-lg border border-amber-200/50 mb-2">
                                                                Using {details.pricing.cupboardIngredientsUsed.length} cupboard ingredients
                                                            </div>
                                                        )}

                                                        <button
                                                            className="w-full mt-4 inline-flex items-center justify-center px-4 py-2.5 rounded-lg text-sm font-semibold bg-amber-50 border-2 border-amber-700/30 text-amber-800 hover:bg-amber-100 transition"
                                                            onClick={() => viewRecipe(recipe.recipeId._id)}
                                                        >
                                                            View Recipe
                                                        </button>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>
                        </div>
                    );
                })}

                {/* Shopping List */}
                {consolidatedIngredients.length > 0 && (
                    <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
                        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 p-4 sm:p-6 border-b border-gray-100">
                            <div>
                                <h2 className="text-lg font-bold text-gray-900 tracking-tight mb-0.5">Shopping List</h2>
                                <p className="text-sm text-gray-500 m-0">Consolidated ingredients for this meal plan</p>
                            </div>
                            <button
                                className={`inline-flex items-center justify-center px-4 py-2.5 rounded-lg text-sm font-semibold min-h-[44px] ${
                                    loadingConsolidatedCost
                                        ? 'bg-gray-100 text-gray-500 cursor-not-allowed'
                                        : 'bg-amber-50 border-2 border-amber-700/30 text-amber-800 hover:bg-amber-100 transition'
                                }`}
                                onClick={calculateConsolidatedCost}
                                disabled={loadingConsolidatedCost}
                            >
                                {loadingConsolidatedCost ? (
                                    <>
                                        <div className="w-4 h-4 border-2 border-gray-200 border-t-amber-500 rounded-full animate-spin mr-2" />
                                        Calculating...
                                    </>
                                ) : (
                                    'Calculate Costs'
                                )}
                            </button>
                        </div>

                        {cupboardItemsCount > 0 && (
                            <div className="mx-4 sm:mx-6 mt-4 p-4 rounded-xl bg-amber-50 border-2 border-amber-200 text-amber-800">
                                <div className="font-bold text-sm mb-0.5">Cupboard Savings Applied</div>
                                <div className="text-sm opacity-95 mb-0.5">
                                    {cupboardItemsCount} ingredient{cupboardItemsCount !== 1 ? 's' : ''} excluded (already in your cupboard)
                                </div>
                                <div className="text-xs opacity-85">
                                    {consolidatedIngredients.filter((ing) => isIngredientInCupboard(ing.name)).map((ing) => ing.name).join(', ')}
                                </div>
                            </div>
                        )}

                        <div className="p-4 sm:p-6">
                            {!showConsolidatedPricing ? (
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                                    {consolidatedIngredients.map((ingredient, index) => (
                                        <div key={index} className="bg-gray-50 rounded-xl border border-gray-100 p-4">
                                            <div className="font-semibold text-gray-900 mb-1">
                                                {ingredient.amounts
                                                    ? ingredient.amounts.map((amt, i) => (
                                                          <span key={i}>
                                                              {amt.amount.toFixed(2)} {amt.unit}
                                                              {i < ingredient.amounts.length - 1 ? ' + ' : ''}
                                                          </span>
                                                      ))
                                                    : `${ingredient.amount?.toFixed(2) || 0} ${ingredient.unit || ''}`}
                                            </div>
                                            <div className="text-gray-600 text-sm mb-1">{ingredient.name}</div>
                                            {ingredient.recipeCount > 1 && (
                                                <span className="inline-flex px-2 py-0.5 rounded bg-green-100 text-green-800 text-[10px] font-medium uppercase">
                                                    Used in {ingredient.recipeCount} recipes
                                                </span>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                showConsolidatedPricing &&
                                !loadingConsolidatedCost &&
                                consolidatedIngredientPrices &&
                                Object.keys(consolidatedIngredientPrices).length > 0 && (
                                    <div>
                                        <div className="mb-6 p-4 bg-gray-50 rounded-xl">
                                            <h3 className="text-gray-900 font-semibold mb-0.5 m-0">Shopping List with Pricing</h3>
                                            <p className="text-gray-600 text-sm m-0">Select your preferred brands and add to shopping list</p>
                                        </div>
                                        <RecipePriceDetails
                                            ingredients={consolidatedIngredients}
                                            ingredientPrices={consolidatedIngredientPrices}
                                            onTotalCostChange={setDisplayTotalCost}
                                            onRequireSignIn={openSignInPopup}
                                        />
                                    </div>
                                )
                            )}
                        </div>
                    </div>
                )}

                {loadingRecipeDetails && (
                    <div className="fixed inset-0 bg-white/90 flex flex-col items-center justify-center z-[1000]">
                        <div className="w-8 h-8 border-2 border-gray-200 border-t-amber-500 rounded-full animate-spin mb-4" />
                        <p className="text-gray-600">Loading recipe details and pricing...</p>
                    </div>
                )}

                <SignInPopup show={showSignInPopup} onClose={handleCloseSignInPopup} />
            </div>
        </div>
    );
};

export default MealPlanDetails;
